import { createFileRoute, Navigate } from '@tanstack/react-router';
import { z } from 'zod';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { useRunProgress } from '~/features/catalog-v2/hooks/use-run-progress';
import { gateRedirectSearch } from '~/features/catalog-v2/utils/run-decisions';

/**
 * Option 2 — the old "Your decision" tab is now the decision panel on top of Products; this route
 * only keeps old links alive: `/gate` → Products (the open gate's panel), `/gate?stage=X` →
 * `decision=X`, and a Content generation `?productId=` → the review lens in focus mode under the
 * collapsed panel line (`gateRedirectSearch`). It renders nothing of its own.
 */
const gateSearchSchema = z.object({
  stage: z.string().optional().catch(undefined),
  productId: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/_authenticated/_tenant/catalog-v2/runs/$runId/gate')({
  validateSearch: gateSearchSchema,
  component: RouteComponent,
});

/** Redirects to Products; waits for `/progress` only when a bare `?productId=` needs the open gate. */
function RouteComponent() {
  const { runId } = Route.useParams();
  const { stage, productId } = Route.useSearch();
  const progress = useRunProgress(runId);
  if (productId && !stage && progress.isLoading) return <PageSkeleton />;
  return (
    <Navigate
      to="/catalog-v2/runs/$runId/products"
      params={{ runId }}
      search={gateRedirectSearch(
        stage ?? null,
        productId ?? null,
        progress.data?.pendingGate?.stage ?? null,
      )}
      replace
    />
  );
}
